import { useTranslation } from "react-i18next";
import { Clock, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import type { ApprovalMetrics } from "./types";

interface MetricItemProps {
  readonly icon: typeof Clock;
  readonly label: string;
  readonly value: string | number;
  readonly danger?: boolean;
}

function MetricItem({ icon: Icon, label, value, danger }: MetricItemProps) {
  return (
    <div className="flex items-center gap-2">
      <Icon
        className={`h-4 w-4 flex-shrink-0 ${danger ? "text-destructive" : "text-muted-foreground"}`}
      />
      <div className="flex flex-col">
        <span
          className={`font-sans text-lg font-semibold ${danger ? "text-destructive" : "text-foreground"}`}
        >
          {value}
        </span>
        <span className="font-sans text-xs font-normal text-muted-foreground">{label}</span>
      </div>
    </div>
  );
}

interface ApprovalMetricsStripProps {
  readonly metrics: ApprovalMetrics;
}

export function ApprovalMetricsStrip({ metrics }: ApprovalMetricsStripProps) {
  const { t } = useTranslation();

  return (
    <div
      data-testid="approval-metrics-strip"
      className="flex flex-wrap gap-6 rounded-lg border border-border/60 bg-card p-4"
    >
      <MetricItem icon={Clock} label={t("venues.metrics.pending")} value={metrics.pending} />
      <MetricItem
        icon={AlertTriangle}
        label={t("venues.metrics.breachingSla")}
        value={metrics.breachingSla}
        danger={metrics.breachingSla > 0}
      />
      <MetricItem
        icon={Clock}
        label={t("venues.metrics.avgTime")}
        value={t("venues.metrics.avgTimeValue", {
          hours: Math.round(metrics.avgHoursToDecision),
        })}
      />
      <MetricItem
        icon={CheckCircle2}
        label={t("venues.metrics.approved")}
        value={metrics.approvedCount}
      />
      <MetricItem
        icon={XCircle}
        label={t("venues.metrics.rejected")}
        value={metrics.rejectedCount}
      />
    </div>
  );
}
